import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Megaphone, Plus, X, Ban } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

const DEFAULT_DAYS = 7;

function fmtUSDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// Own status label, not just active/inactive -- "Retracted" (an admin
// pulled it early) reads very differently from "Expired" (it simply ran
// its course), and a dispatcher-facing history list should tell those
// apart rather than collapsing both into one generic "inactive" state.
function statusOf(row, now) {
  if (!row.active) return { label: "Retracted", tone: "muted" };
  if (new Date(row.expires_at).getTime() <= now) return { label: "Expired", tone: "muted" };
  return { label: "Active", tone: "live" };
}

// Founder-facing tool for the sticky banner every dispatcher sees on
// Dispatch home + Operational Center (see AnnouncementBanner.jsx). This
// page is the only place a message gets created -- dispatchers only ever
// read, never write (see migration 0071's own ACL rows). A message can't
// be permanently dismissed by a dispatcher (the banner reappears on their
// next page load) so it naturally keeps surfacing until duration_days
// runs out, or an admin retracts it early from here.
export default function Announcements() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState("");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [retractingId, setRetractingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/dispatch_announcements/?limit=200`, authHeaders())
      .then((res) => {
        const list = res.data.records || res.data || [];
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRows(list);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function publish() {
    const trimmed = message.trim();
    const n = Number(days);
    if (!trimmed) { setFormError("Write the message first."); return; }
    if (!Number.isFinite(n) || n < 1) { setFormError("Show-for days must be at least 1."); return; }
    setFormError(null);
    setSubmitting(true);
    const expiresAt = new Date(Date.now() + n * 24 * 60 * 60 * 1000).toISOString();
    axios
      .post(
        `${apiBaseUrl}/dispatch_announcements/`,
        { message: trimmed, duration_days: n, expires_at: expiresAt, active: true },
        authHeaders()
      )
      .then(() => {
        setMessage("");
        setDays(DEFAULT_DAYS);
        setShowForm(false);
        load();
      })
      .catch((err) => setFormError(err.response?.data?.detail || err.message))
      .finally(() => setSubmitting(false));
  }

  function retract(row) {
    if (!window.confirm("Retract this announcement now? It'll stop showing to the team immediately.")) return;
    setRetractingId(row.id);
    axios
      .put(`${apiBaseUrl}/dispatch_announcements/${row.id}`, { active: false }, authHeaders())
      .then(() => load())
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setRetractingId(null));
  }

  const now = Date.now();

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Announcements</h1>
          <div style={styles.subtitle}>
            A sticky banner every dispatcher sees on Dispatch home and Operational Center -- use it to tell the team
            about a change, a fix, or planned downtime without any real downtime to the system. Set how many days it
            should keep showing; a dispatcher can dismiss it from their own screen, but it comes back on their next
            visit until the days run out (or you retract it here).
          </div>
        </div>
        <button
          style={styles.addBtn}
          onClick={() => { setShowForm((s) => !s); setFormError(null); }}
        >
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New announcement"}
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {showForm && (
        <div style={styles.formCard}>
          {formError && <div style={styles.errorBox}>{formError}</div>}
          <div style={styles.formRow}>
            <label style={styles.formLabel}>Message</label>
            <textarea
              style={styles.formTextarea}
              rows={3}
              placeholder="e.g. We fixed the fuel stop matching bug reported this week -- planned vs actual fuel costs should now line up correctly."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <div style={styles.formRow}>
            <label style={styles.formLabel}>Show this for</label>
            <div style={styles.daysRow}>
              <input
                type="number"
                min={1}
                style={styles.daysInput}
                value={days}
                onChange={(e) => setDays(e.target.value)}
              />
              <span style={styles.daysSuffix}>day{Number(days) === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button style={styles.submitBtn} onClick={publish} disabled={submitting}>
            {submitting ? "Publishing…" : "Publish to the team"}
          </button>
        </div>
      )}

      {loading ? (
        <div style={styles.emptyState}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={styles.emptyState}>No announcements yet -- anything you publish here will show up to the whole team.</div>
      ) : (
        <div style={styles.list}>
          {rows.map((row) => {
            const status = statusOf(row, now);
            return (
              <div key={row.id} style={styles.row}>
                <div style={styles.rowIcon}>
                  <Megaphone size={15} />
                </div>
                <div style={styles.rowBody}>
                  <div style={styles.rowMessage}>{row.message}</div>
                  <div style={styles.rowMeta}>
                    Published {fmtUSDateTime(row.created_at)} · {row.duration_days} day{row.duration_days === 1 ? "" : "s"}
                    {" "}· {status.label === "Expired" ? "expired" : "expires"} {fmtUSDateTime(row.expires_at)}
                  </div>
                </div>
                <div style={{ ...styles.statusPill, ...(status.tone === "live" ? styles.statusLive : styles.statusMuted) }}>
                  {status.label}
                </div>
                {status.label === "Active" && (
                  <button
                    style={styles.retractBtn}
                    onClick={() => retract(row)}
                    disabled={retractingId === row.id}
                    title="Retract now"
                  >
                    <Ban size={13} /> {retractingId === row.id ? "Retracting…" : "Retract"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 640, lineHeight: 1.5 },
  addBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "#fff",
    background: "var(--brand)", border: "none", borderRadius: 8, padding: "9px 16px", cursor: "pointer",
    flexShrink: 0,
  },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  formCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", padding: "18px 20px", marginBottom: 20, maxWidth: 640,
    display: "flex", flexDirection: "column", gap: 14,
  },
  formRow: { display: "flex", flexDirection: "column", gap: 6 },
  formLabel: { fontSize: 12, fontWeight: 700, color: "var(--ink-2)" },
  formTextarea: {
    fontSize: 13.5, color: "var(--ink)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "9px 11px", width: "100%", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
  },
  daysRow: { display: "flex", alignItems: "center", gap: 8 },
  daysInput: {
    fontSize: 13.5, color: "var(--ink)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "8px 10px", width: 80,
  },
  daysSuffix: { fontSize: 13, color: "var(--muted)" },
  submitBtn: {
    fontSize: 13, fontWeight: 700, color: "#fff", background: "var(--brand)", border: "none", borderRadius: 8,
    padding: "9px 18px", cursor: "pointer", alignSelf: "flex-start",
  },

  emptyState: {
    fontSize: 13, color: "var(--muted)", background: "var(--card)", border: "1px dashed var(--line)",
    borderRadius: "var(--radius-lg)", padding: "24px", textAlign: "center", maxWidth: 640,
  },

  list: { display: "flex", flexDirection: "column", gap: 8, maxWidth: 760 },
  row: {
    display: "flex", alignItems: "flex-start", gap: 12,
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
    padding: "14px 16px", boxShadow: "var(--shadow-xs)",
  },
  rowIcon: {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center",
    justifyContent: "center", color: "var(--brand)", background: "rgba(37,99,235,0.08)", marginTop: 1,
  },
  rowBody: { flex: 1, minWidth: 0 },
  rowMessage: { fontSize: 13.5, color: "var(--ink)", lineHeight: 1.5, whiteSpace: "pre-wrap" },
  rowMeta: { fontSize: 11.5, color: "var(--muted)", marginTop: 6 },
  statusPill: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em",
    borderRadius: 999, padding: "3px 10px", flexShrink: 0, marginTop: 1,
  },
  statusLive: { color: "#16a34a", background: "rgba(22,163,74,0.1)" },
  statusMuted: { color: "var(--muted)", background: "var(--bg)" },
  retractBtn: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "#dc2626",
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 6,
    padding: "5px 9px", cursor: "pointer", flexShrink: 0, marginTop: 1,
  },
};
